import {Controller} from "stimulus"

/**
 * Stimulus controller for the FWA goal completion modal.
 * Dynamically renders question inputs based on goal's additional questions config.
 *
 * Modal config shape (from data-modal-config JSON):
 * {
 *   type: "questions",
 *   title: "Goal Title",
 *   questions: [
 *     { "question-id": 5, label: "...", "input-type": "number", options: [...], validation: {...} },
 *     ...
 *   ]
 * }
 *
 * Input types: "text", "number", "select", "boolean", "range"
 */

// Mirrors the `debt-types` vector in fwa_dashboard.clj — keep in sync.
// Canonical debt-type slugs that have a tab in the modal. Any pre-fill row
// whose debt-type is outside this set is routed to "other" so legacy
// assessments (whose rows were imported with debt-type "allDebts" /
// "highInterestDebts" before the import fix) still render and can be edited.
const KNOWN_DEBT_TYPES = new Set(["credit-cards", "mortgage", "auto-loans", "student-loans", "medical-debt", "other"])

export default class extends Controller {
  static get targets() {
    return [
      "overlay",
      "card",
      "title",
      "subtitle",
      "questionsTemplate",
      "questionsContainer",
      "debtsTemplate",
      "debtsInstruction",
      "debtTab",
      "debtPanel",
      "debtEntriesContainer",
      "debtEntry",
      "debtAmount",
      "debtPayment",
      "debtRate",
      "liveRegion",
      "errorMessage"
    ]
  }

  connect() {
    this._boundHandleGoalCompleted = this._handleGoalCompleted.bind(this)
    this._boundHandleKeydown = this._handleKeydown.bind(this)
    document.addEventListener("fwa-goal-completed", this._boundHandleGoalCompleted)
    this._answers = {}
    this._questions = []
    this._goalId = null
    this._runUuid = null
    this._nextEntryId = 1
  }

  disconnect() {
    document.removeEventListener("fwa-goal-completed", this._boundHandleGoalCompleted)
  }

  _currentValue(q) {
    const v = q["current-value"]
    return v == null || v === "" ? null : v
  }

  _handleGoalCompleted(event) {
    const {config, triggerElement, goalId, runUuid} = event.detail || {}
    if (!config) return

    let parsed
    try {
      parsed = typeof config === "string" ? JSON.parse(config) : config
    } catch (e) {
      console.warn("Failed to parse modal config:", e)
      return
    }

    this._triggerElement = triggerElement || document.activeElement
    this._goalId = goalId
    this._runUuid = runUuid
    this._answers = {}

    if (parsed.type === "questions" && parsed.questions && parsed.questions.length > 0) {
      return this._showQuestions(parsed)
    }
    if (parsed.type === "debts") {
      return this._showDebts(parsed)
    }
    // Unknown type — do nothing.
  }

  _showQuestions(config) {
    this.titleTarget.textContent = config.title || ""
    const anyPrefilled = (config.questions || []).some((q) => this._currentValue(q) != null)
    const subtitle = anyPrefilled ? "Confirm or update your financial profile." : "Great job! Please answer these questions to update your financial profile."
    this.subtitleTarget.textContent = subtitle

    // Show questions template, hide debts template
    if (this.hasQuestionsTemplateTarget) this.questionsTemplateTarget.classList.remove("hidden")
    if (this.hasDebtsTemplateTarget) this.debtsTemplateTarget.classList.add("hidden")

    // Questions mode stays at default card width
    if (this.hasCardTarget) this.cardTarget.classList.remove("fwa-modal-card--wide")

    this._questions = config.questions || []

    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }

    this.questionsContainerTarget.innerHTML = ""

    config.questions.forEach((q) => {
      const questionEl = this._renderQuestion(q)
      this.questionsContainerTarget.appendChild(questionEl)
    })

    this.overlayTarget.classList.remove("hidden")
    this.overlayTarget.classList.add("fwa-modal-visible")
    document.body.style.overflow = "hidden"
    document.removeEventListener("keydown", this._boundHandleKeydown)
    document.addEventListener("keydown", this._boundHandleKeydown)

    requestAnimationFrame(() => {
      this._trapFocus()
    })
  }

  _showDebts(config) {
    this.titleTarget.textContent = config.title || "Update Your Debts"
    this.subtitleTarget.textContent = config.subtitle || "Congratulations on reaching another goal! If your debts have changed, you can update them below."

    if (this.hasDebtsInstructionTarget) {
      this.debtsInstructionTarget.textContent = config.instruction || "Enter your debts. If it doesn't apply, leave the section blank."
    }

    // Show debts template, hide questions template
    if (this.hasQuestionsTemplateTarget) this.questionsTemplateTarget.classList.add("hidden")
    if (this.hasDebtsTemplateTarget) this.debtsTemplateTarget.classList.remove("hidden")

    // Widen the card for the tabs + form layout
    if (this.hasCardTarget) this.cardTarget.classList.add("fwa-modal-card--wide")

    // Clear stale entries from any prior open
    this.debtEntriesContainerTargets.forEach((c) => {
      c.innerHTML = ""
    })

    // Group :initial-debts by debt-type. Unknown types route to "other" so
    // legacy assessment rows (debt_type "allDebts" / "highInterestDebts") still
    // render and are editable.
    const initial = config["initial-debts"] || []
    const byType = {}
    initial.forEach((d) => {
      const raw = d["debt-type"]
      const type = KNOWN_DEBT_TYPES.has(raw) ? raw : "other"
      if (!byType[type]) byType[type] = []
      byType[type].push(d)
    })

    // For each per-type container, append one entry per pre-filled debt;
    // if there are none for this type, append a single empty entry so the panel
    // isn't visually empty when selected.
    this.debtEntriesContainerTargets.forEach((container) => {
      const type = container.getAttribute("data-debt-type")
      const rows = byType[type] || []
      if (rows.length === 0) {
        this._appendDebtEntry(container, {})
      } else {
        rows.forEach((d) => {
          this._appendDebtEntry(container, {
            amountOwed: d["amount-owed"],
            monthlyPayment: d["monthly-payment"],
            interestRate: d["interest-rate"]
          })
        })
      }
    })

    // Pick active tab: first debt-type (in markup order) with pre-filled rows,
    // else credit-cards.
    const firstWithData = this.debtPanelTargets.map((p) => p.getAttribute("data-debt-type")).find((t) => (byType[t] || []).length > 0)
    this._setActiveDebtTab(firstWithData || "credit-cards")

    // Show overlay (same pattern as _showQuestions)
    this.overlayTarget.classList.remove("hidden")
    this.overlayTarget.classList.add("fwa-modal-visible")
    document.body.style.overflow = "hidden"
    document.removeEventListener("keydown", this._boundHandleKeydown)
    document.addEventListener("keydown", this._boundHandleKeydown)
    requestAnimationFrame(() => this._trapFocus())
  }

  _renderQuestion(q) {
    const wrapper = document.createElement("div")
    wrapper.className = "fwa-modal-question"

    const questionId = q["question-id"]
    const inputType = q["input-type"]
    const label = document.createElement("label")
    label.className = "fwa-modal-question-label"
    label.textContent = q.label || ""
    wrapper.appendChild(label)

    switch (inputType) {
      case "text":
        wrapper.appendChild(this._renderTextInput(questionId, q))
        break
      case "number":
        wrapper.appendChild(this._renderNumberInput(questionId, q))
        break
      case "select":
        wrapper.appendChild(this._renderSelectButtons(questionId, q))
        break
      case "boolean":
        wrapper.appendChild(this._renderBooleanButtons(questionId, q))
        break
      case "range":
        wrapper.appendChild(this._renderRangeSlider(questionId, q))
        break
      default:
        wrapper.appendChild(this._renderTextInput(questionId, q))
    }

    return wrapper
  }

  _renderTextInput(questionId, q) {
    const input = document.createElement("input")
    input.type = "text"
    input.className = "fwa-modal-input"
    input.placeholder = q.placeholder || ""
    const pv = this._currentValue(q)
    if (pv != null) {
      input.value = String(pv)
      this._answers[questionId] = input.value
    }
    input.addEventListener("input", () => {
      this._answers[questionId] = input.value
    })
    return input
  }

  _renderNumberInput(questionId, q) {
    const input = document.createElement("input")
    input.type = "number"
    input.className = "fwa-modal-input"
    input.inputMode = "numeric"
    if (q.validation) {
      if (q.validation.min != null) input.min = q.validation.min
      if (q.validation.max != null) input.max = q.validation.max
      if (q.validation.step != null) input.step = q.validation.step
    }
    const pv = this._currentValue(q)
    if (pv != null) {
      input.value = String(pv)
      this._answers[questionId] = input.value
    }
    input.addEventListener("input", () => {
      this._answers[questionId] = input.value
    })
    return input
  }

  _renderSelectButtons(questionId, q) {
    const group = document.createElement("div")
    group.className = "fwa-modal-btn-group"

    const options = q.options || []
    const pv = this._currentValue(q)
    const pvStr = pv == null ? null : String(pv)

    options.forEach((opt) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "fwa-modal-btn-option"
      btn.textContent = opt.label || opt.value
      btn.setAttribute("data-value", opt.value)

      if (pvStr != null && String(opt.value) === pvStr) {
        btn.classList.add("fwa-modal-btn-option--selected")
        this._answers[questionId] = opt.value
      }

      btn.addEventListener("click", () => {
        group.querySelectorAll(".fwa-modal-btn-option").forEach((b) => {
          b.classList.remove("fwa-modal-btn-option--selected")
        })
        btn.classList.add("fwa-modal-btn-option--selected")
        this._answers[questionId] = opt.value
      })

      group.appendChild(btn)
    })

    return group
  }

  _coerceBooleanValue(v) {
    if (v === true || v === "true" || v === "yes" || v === 1) return "true"
    if (v === false || v === "false" || v === "no" || v === 0) return "false"
    return null
  }

  _renderBooleanButtons(questionId, q) {
    const group = document.createElement("div")
    group.className = "fwa-modal-btn-group"

    const pv = this._currentValue(q)
    const pvStr = this._coerceBooleanValue(pv)

    ;[
      {value: "true", label: "Yes"},
      {value: "false", label: "No"}
    ].forEach((opt) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "fwa-modal-btn-option"
      btn.textContent = opt.label
      btn.setAttribute("data-value", opt.value)

      if (pvStr === opt.value) {
        btn.classList.add("fwa-modal-btn-option--selected")
        this._answers[questionId] = opt.value
      }

      btn.addEventListener("click", () => {
        group.querySelectorAll(".fwa-modal-btn-option").forEach((b) => {
          b.classList.remove("fwa-modal-btn-option--selected")
        })
        btn.classList.add("fwa-modal-btn-option--selected")
        this._answers[questionId] = opt.value
      })

      group.appendChild(btn)
    })

    return group
  }

  _renderRangeSlider(questionId, q) {
    const container = document.createElement("div")

    const validation = q.validation || {}
    const min = validation.min != null ? validation.min : 1
    const max = validation.max != null ? validation.max : 5
    const step = validation.step != null ? validation.step : 1
    const midpoint = Math.round((min + max) / 2)

    const pv = this._currentValue(q)
    const pvNum = pv == null ? null : Number(pv)
    const startVal = pvNum != null && Number.isFinite(pvNum) && pvNum >= min && pvNum <= max ? pvNum : midpoint

    const valueDisplay = document.createElement("div")
    valueDisplay.className = "fwa-modal-range-value"
    valueDisplay.textContent = startVal
    container.appendChild(valueDisplay)

    const slider = document.createElement("input")
    slider.type = "range"
    slider.className = "fwa-modal-range"
    slider.min = min
    slider.max = max
    slider.step = step
    slider.value = startVal
    this._answers[questionId] = String(startVal)

    slider.addEventListener("input", () => {
      valueDisplay.textContent = slider.value
      this._answers[questionId] = slider.value
    })
    container.appendChild(slider)

    const labels = document.createElement("div")
    labels.className = "fwa-modal-range-labels"
    const minLabel = document.createElement("span")
    minLabel.textContent = min
    const maxLabel = document.createElement("span")
    maxLabel.textContent = max
    labels.appendChild(minLabel)
    labels.appendChild(maxLabel)
    container.appendChild(labels)

    return container
  }

  // --- Actions ---

  close() {
    this.overlayTarget.classList.remove("fwa-modal-visible")
    this.overlayTarget.classList.add("hidden")
    document.body.style.overflow = ""
    document.removeEventListener("keydown", this._boundHandleKeydown)

    if (this.hasCardTarget) {
      this.cardTarget.classList.remove("fwa-modal-card--wide")
    }

    // Hide both inner templates so neither is visible on the next open
    // (the appropriate one is re-shown by _showQuestions / _showDebts).
    if (this.hasQuestionsTemplateTarget) this.questionsTemplateTarget.classList.add("hidden")
    if (this.hasDebtsTemplateTarget) this.debtsTemplateTarget.classList.add("hidden")

    // Clear any debt entries so they don't leak into the next open
    this.debtEntriesContainerTargets.forEach((c) => {
      c.innerHTML = ""
    })

    if (this._triggerElement && this._triggerElement.focus) {
      this._triggerElement.focus()
    }
    this._triggerElement = null
    this._goalId = null
    this._runUuid = null
    this._answers = {}
    this._questions = []
    this._nextEntryId = 1
  }

  update() {
    // Debts mode: debtsTemplate is the visible template
    if (this.hasDebtsTemplateTarget && !this.debtsTemplateTarget.classList.contains("hidden")) {
      const debts = []
      this.debtEntriesContainerTargets.forEach((container) => {
        const type = container.getAttribute("data-debt-type")
        container.querySelectorAll(".fwa-debt-entry").forEach((entry) => {
          const amountInput = entry.querySelector('[data-field="amountOwed"]')
          const paymentInput = entry.querySelector('[data-field="monthlyPayment"]')
          const rateInput = entry.querySelector('[data-field="interestRate"]')

          const amountOwed = this._parseCurrency(amountInput && amountInput.value)
          const monthlyPayment = this._parseCurrency(paymentInput && paymentInput.value)
          const interestRate = this._parseRate(rateInput && rateInput.value)

          // Skip rows that are entirely empty
          if (amountOwed === 0 && monthlyPayment === 0 && interestRate == null) return

          debts.push({
            "debt-type": type,
            "amount-owed": amountOwed,
            "monthly-payment": monthlyPayment,
            "interest-rate": interestRate
          })
        })
      })

      document.dispatchEvent(
        new CustomEvent("fwa-goal-answers-submitted", {
          detail: {goalId: this._goalId, runUuid: this._runUuid, debts}
        })
      )

      if (this.hasLiveRegionTarget) {
        this.liveRegionTarget.textContent = "Debts updated"
      }
      this.close()
      return
    }

    // Questions mode — existing logic
    const answers = this._questions
      .map((q) => {
        const questionId = q["question-id"]
        const value = this._answers[questionId]
        if (value === "" || value == null) return null
        if (q.section && q.field) {
          return {section: q.section, field: q.field, value}
        }
        return {"question-id": parseInt(questionId, 10), "answer-value": value}
      })
      .filter((a) => a != null)

    const allAnswered = this._questions.every((q) => {
      const val = this._answers[q["question-id"]]
      return val !== "" && val != null
    })

    if (!allAnswered) {
      if (this.hasErrorMessageTarget) {
        this.errorMessageTarget.classList.remove("hidden")
      }
      return
    }

    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }

    document.dispatchEvent(
      new CustomEvent("fwa-goal-answers-submitted", {
        detail: {goalId: this._goalId, runUuid: this._runUuid, answers}
      })
    )

    if (this.hasLiveRegionTarget) {
      this.liveRegionTarget.textContent = "Financial information updated."
    }

    this.close()
  }

  backdropClick(event) {
    if (this.hasCardTarget && !this.cardTarget.contains(event.target)) {
      this.close()
    }
  }

  // --- Debt value formatting / parsing ---

  _formatCurrency(n) {
    if (n == null || n === "") return ""
    const num = Number(n)
    if (!Number.isFinite(num)) return ""
    return "$" + num.toLocaleString("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2})
  }

  _formatPercent(n) {
    if (n == null || n === "") return ""
    const num = Number(n)
    if (!Number.isFinite(num)) return ""
    return num + "%"
  }

  _parseCurrency(s) {
    if (s == null || s === "") return 0
    const cleaned = String(s).replace(/[$,\s]/g, "")
    const n = parseFloat(cleaned)
    return Number.isFinite(n) && n >= 0 ? n : 0
  }

  _parseRate(s) {
    if (s == null || s === "") return null
    const cleaned = String(s).replace(/%/g, "").trim()
    if (cleaned === "") return null
    const n = parseFloat(cleaned)
    return Number.isFinite(n) ? n : null
  }

  // --- Debt entry DOM construction ---

  _appendDebtEntry(container, {amountOwed, monthlyPayment, interestRate} = {}) {
    const idx = this._nextEntryId++
    const entry = document.createElement("div")
    entry.className = "fwa-debt-entry border border-neutral-13 rounded-xl p-4 flex flex-col gap-4 relative"
    entry.setAttribute("data-target", "fwa-modal.debtEntry")
    entry.setAttribute("data-entry-index", String(idx))

    entry.innerHTML = `
      <button type="button" class="absolute text-neutral-7 hover:text-neutral-1 transition-colors"
              style="top: 12px; right: 12px;" aria-label="Remove this debt entry"
              data-action="fwa-modal#removeDebtEntry">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
             fill="none" aria-hidden="true">
          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                fill="currentColor"/>
        </svg>
      </button>
      <div>
        <label class="fwa-modal-question-label" for="debt-amount-${idx}">How much do you owe?</label>
        <input id="debt-amount-${idx}" type="text" placeholder="$0" class="fwa-modal-input"
               data-field="amountOwed" data-target="fwa-modal.debtAmount" />
      </div>
      <div>
        <label class="fwa-modal-question-label" for="debt-payment-${idx}">What's your monthly payment?</label>
        <input id="debt-payment-${idx}" type="text" placeholder="$0" class="fwa-modal-input"
               data-field="monthlyPayment" data-target="fwa-modal.debtPayment" />
      </div>
      <div>
        <label class="fwa-modal-question-label" for="debt-rate-${idx}">What's the interest rate?</label>
        <input id="debt-rate-${idx}" type="text" placeholder="0%" class="fwa-modal-input"
               data-field="interestRate" data-target="fwa-modal.debtRate" />
      </div>
    `

    entry.querySelector('[data-field="amountOwed"]').value = this._formatCurrency(amountOwed)
    entry.querySelector('[data-field="monthlyPayment"]').value = this._formatCurrency(monthlyPayment)
    entry.querySelector('[data-field="interestRate"]').value = this._formatPercent(interestRate)

    container.appendChild(entry)
    return entry
  }

  // --- Debt tab switching ---

  _setActiveDebtTab(typeId) {
    if (!this.hasDebtTabTarget) return

    this.debtTabTargets.forEach((tab) => {
      const isSelected = tab.getAttribute("data-debt-type") === typeId
      tab.setAttribute("aria-selected", isSelected ? "true" : "false")
      // Selected = visible card-style border; unselected = transparent border
      // (preserves layout) with hover-darken cue.
      tab.classList.toggle("border-neutral-13", isSelected)
      tab.classList.toggle("border-transparent", !isSelected)
      tab.classList.toggle("hover:border-neutral-13", !isSelected)
    })

    this.debtPanelTargets.forEach((panel) => {
      const isActive = panel.getAttribute("data-debt-type") === typeId
      panel.classList.toggle("hidden", !isActive)
    })
  }

  selectDebtTab(event) {
    const typeId = event.currentTarget.getAttribute("data-debt-type")
    this._setActiveDebtTab(typeId)
  }

  addDebtEntry(event) {
    const typeId = event.currentTarget.getAttribute("data-debt-type")
    const container = this.debtEntriesContainerTargets.find((c) => c.getAttribute("data-debt-type") === typeId)
    if (!container) return

    this._appendDebtEntry(container, {})

    if (this.hasLiveRegionTarget) {
      this.liveRegionTarget.textContent = "Debt entry added"
    }
  }

  removeDebtEntry(event) {
    event.stopPropagation()
    const entry = event.currentTarget.closest(".fwa-debt-entry")
    if (!entry) return

    const container = entry.parentElement
    const entries = container.querySelectorAll(".fwa-debt-entry")
    if (entries.length <= 1) return // Always keep at least one entry per tab

    entry.remove()

    if (this.hasLiveRegionTarget) {
      this.liveRegionTarget.textContent = "Debt entry removed"
    }
  }

  // --- Keyboard ---

  _handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      return
    }
    if (event.key === "Tab") {
      this._handleTabTrap(event)
    }
  }

  // --- Focus Trap ---

  _trapFocus() {
    const focusable = this._getFocusableElements()
    if (focusable.length > 0) {
      focusable[0].focus()
    }
  }

  _getFocusableElements() {
    if (!this.hasCardTarget) return []
    return Array.from(
      this.cardTarget.querySelectorAll('button:not([disabled]):not([hidden]), input:not([disabled]):not([hidden]), [tabindex]:not([tabindex="-1"]):not([hidden])')
    ).filter((el) => el.offsetParent !== null)
  }

  _handleTabTrap(event) {
    const focusable = this._getFocusableElements()
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault()
        last.focus()
      }
    } else {
      if (document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
  }
}
